import { Area, AreaChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { MODEL_TIERS, type ModelTier, type SpendSummary, type Tool } from '@ace-tracker/shared';
import { CHART_SERIES_COLORS, chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { SPEND_METRIC_INFO } from '../metricInfo';
import { useSettings } from '../settings';
import { ChartCard } from './ChartCard';
import { TOOL_LABELS } from './Sidebar';
import { TableCard } from './TableCard';

const TIER_LABELS: Record<ModelTier, string> = { fast: 'Fast', mid: 'Mid', frontier: 'Frontier' };
const TIER_COLORS: Record<ModelTier, string> = { fast: CHART_SERIES_COLORS[2], mid: CHART_SERIES_COLORS[0], frontier: CHART_SERIES_COLORS[1] };

const pct = (share: number | null) => (share === null ? '—' : `${(share * 100).toFixed(share < 0.1 && share > 0 ? 1 : 0)}%`);

/** Model tier mix (E1.10): cost share by tier overall and per day, token share by tier per tool. */
export function TierMixSection({
  summary,
  money,
  SectionHeading,
}: {
  summary: SpendSummary;
  money: (value: number) => string;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const { data: settings } = useSettings();
  const goal = settings?.targets.frontierShareMaxPercent;
  const tierOf = new Map(summary.byModel.map((m) => [`${m.tool}\u0000${m.model}`, m.tier]));
  const costByTier = Object.fromEntries(MODEL_TIERS.map((t) => [t, 0])) as Record<ModelTier, number>;
  for (const m of summary.byModel) costByTier[m.tier] += m.cost;
  const total = summary.byModel.reduce((sum, m) => sum + m.cost, 0);
  const frontierShare = total > 0 ? costByTier.frontier / total : null;

  const byDay = new Map<string, Record<ModelTier, number>>();
  for (const point of summary.daily) {
    const tier = tierOf.get(`${point.tool}\u0000${point.model}`) ?? 'mid';
    const day = byDay.get(point.date) ?? { fast: 0, mid: 0, frontier: 0 };
    day[tier] += point.cost;
    byDay.set(point.date, day);
  }
  const daily = [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, costs]) => {
      const dayTotal = costs.fast + costs.mid + costs.frontier;
      return { date, ...Object.fromEntries(MODEL_TIERS.map((t) => [t, dayTotal > 0 ? (costs[t] / dayTotal) * 100 : 0])) };
    });

  const tools = [...new Set(summary.byModel.map((m) => m.tool as Tool))];
  const tokenShare = tools.map((tool) => {
    const models = summary.byModel.filter((m) => m.tool === tool);
    const tokens = (m: (typeof models)[number]) => m.tokens.input + m.tokens.output + m.tokens.cacheRead + m.tokens.cacheWrite;
    const toolTokens = models.reduce((sum, m) => sum + tokens(m), 0);
    const toolCost = models.reduce((sum, m) => sum + m.cost, 0);
    return {
      tool,
      byTier: MODEL_TIERS.map((tier) => {
        const inTier = models.filter((m) => m.tier === tier);
        return {
          tier,
          tokens: toolTokens > 0 ? inTier.reduce((sum, m) => sum + tokens(m), 0) / toolTokens : null,
          cost: toolCost > 0 ? inTier.reduce((sum, m) => sum + m.cost, 0) / toolCost : null,
        };
      }),
    };
  });

  const overGoal = goal !== undefined && frontierShare !== null && frontierShare * 100 > goal;

  return (
    <section>
      <SectionHeading
        title="Model tier mix"
        hint={
          goal === undefined
            ? `Frontier models are ${pct(frontierShare)} of spend`
            : `Frontier models are ${pct(frontierShare)} of spend · goal ≤ ${goal}%${overGoal ? ' — above goal' : ' — met'}`
        }
        info={SPEND_METRIC_INFO.tierMix}
      />
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {MODEL_TIERS.map((tier) => (
          <div key={tier} className="relative rounded-card border border-border bg-surface p-4 shadow-sm">
            <span className="absolute inset-y-0 left-0 w-[3px] rounded-l-card" style={{ backgroundColor: TIER_COLORS[tier] }} />
            <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">{TIER_LABELS[tier]}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-text-default">{pct(total > 0 ? costByTier[tier] / total : null)}</p>
            <p className="text-xs text-text-muted">{money(costByTier[tier])} of spend</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <ChartCard title="Cost share by tier, per day" caption="Share of each day's indicative cost" info={SPEND_METRIC_INFO.tierMix}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily} margin={{ left: 8, right: 12, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
              <XAxis dataKey="date" {...chartXAxisTickProps} />
              <YAxis {...chartAxisTickProps} width={48} domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
              <Tooltip {...chartTooltipProps} formatter={((value: number) => `${value.toFixed(1)}%`) as never} />
              <Legend {...chartLegendProps} />
              {MODEL_TIERS.map((tier) => (
                <Area key={tier} type="monotone" dataKey={tier} name={TIER_LABELS[tier]} stackId="tier" stroke={TIER_COLORS[tier]} fill={TIER_COLORS[tier]} fillOpacity={0.35} isAnimationActive={false} />
              ))}
              {goal !== undefined && (
                <ReferenceLine
                  y={100 - goal}
                  stroke="var(--text-muted)"
                  strokeDasharray="5 4"
                  label={{ value: `Frontier goal ≤ ${goal}%`, position: 'insideBottomLeft', fill: 'var(--text-muted)', fontSize: 11 }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <TableCard title="Tier share per tool" info={SPEND_METRIC_INFO.tierMix} empty={tokenShare.length === 0} emptyText="No token usage in this range.">
          <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
            <tr>
              <th className="pb-2.5 pr-3">Tool</th>
              <th className="pb-2.5 pr-3">Tier</th>
              <th className="pb-2.5 pr-3 text-right">Share of tokens</th>
              <th className="pb-2.5 text-right">Share of cost</th>
            </tr>
          </thead>
          <tbody>
            {tokenShare.flatMap((row) =>
              row.byTier.map((t) => (
                <tr key={`${row.tool}:${t.tier}`} className="border-b border-border last:border-0">
                  <td className="py-2.5 pr-3 text-text-muted">{TOOL_LABELS[row.tool]}</td>
                  <td className="py-2.5 pr-3 text-text-default">{TIER_LABELS[t.tier]}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{pct(t.tokens)}</td>
                  <td className="py-2.5 text-right tabular-nums">{pct(t.cost)}</td>
                </tr>
              )),
            )}
          </tbody>
        </TableCard>
      </div>
    </section>
  );
}
